import { Injectable } from '@angular/core';
import { CssUtils } from '@common/flare-data/util/css-utils';
import { validateXml } from '@common/html/util/xml';
import { dirname, tryUpdateRelativeLink } from '@common/util/path';
import { ProjectFileType } from '@portal-core/project-files/enums/project-file-type.enum';
import { ProjectFilesService } from '@portal-core/project-files/services/project-files.service';


@Injectable({
  providedIn: 'root'
})
export class ProjectFilesLinkService {

  constructor(
    private projectFilesService: ProjectFilesService,
  ) { }

  /**
   * Updates relative links in the file content.
   * @param content Represents the file content.
   * @param originalFilePath Represents an original file path.
   * @param newFilePath Represents a new path of the moved file.
   * @returns Returns the file content with updated relative links corresponding to a new file path.
   */
  updateContentLinks(content: string, originalFilePath: string, newFilePath: string): string {
    const fileType = this.projectFilesService.getFileType(originalFilePath);

    if (fileType === ProjectFileType.Stylesheet) {
      return this.updateCssLinks(content, originalFilePath, newFilePath);
    }

    // try parse content to xml document
    let doc = this.parseToDocument(content);
    if (!doc)
      return content;

    this.updateDocumentLinks(doc, originalFilePath, newFilePath);
    var xmlSerializer = new XMLSerializer();
    return xmlSerializer.serializeToString(doc);
  }

  /**
   * Updates relative links in the specified document.
   * @param doc Document to update relative links.
   * @param originalFilePath Represents an original file path.
   * @param newFilePath Represents a new path of the moved file.
   */
  updateDocumentLinks(doc: Document, originalFilePath: string, newFilePath: string): void {
    const sourceFolder = dirname(originalFilePath);
    const targetFolder = dirname(newFilePath);
    if (sourceFolder === targetFolder)
      return;

    const type = this.projectFilesService.getFileType(originalFilePath);
    // update links in topic file
    if (type === ProjectFileType.Flare)
      this.updateFlareFileLinks(doc, originalFilePath, targetFolder);
    // update links in TOC file
    if (type === ProjectFileType.Toc)
      this.updateTocFileLinks(doc, originalFilePath, targetFolder);
  }

  /**
   * Updates relative links in the specified CSS content.
   * @param content CSS content where to update relative links.
   * @param originalFilePath Represents an original file path.
   * @param newFilePath Represents a new path of the moved file.
   * @returns Returns the CSS content with updated relative links corresponding to a new file path.
   */
  updateCssLinks(content: string, originalFilePath: string, newFilePath: string): string {
    const sourceFolder = dirname(originalFilePath);
    const targetFolder = dirname(newFilePath);
    if (sourceFolder === targetFolder)
      return content;
    return this.updateRelativeLinksInCssStyle(content, originalFilePath, targetFolder);
  }

  private parseToDocument(content: string): Document {
    return validateXml(content, {
      validateXmlDeclaration: false
    }).doc;
  }

  private updateFlareFileLinks(doc: Document, filePath: string, targetFolder: string) {
    const attrs = ['style', 'href', 'src', 'dynsrc', 'altsrc', 'MadCap:altsrc', 'movie', 'value', 'MadCap:src', 'MadCap:mediastyle', 'MadCap:thumbnail-src', 'MadCap:link'];

    this.iterateNodes(doc.childNodes, (node: Element) => {
      if (node.nodeName === 'style') {
        this.updateRelativeLinksInLocalStyle(node as HTMLStyleElement, filePath, targetFolder);
      }
      if (node.hasAttributes) {
        attrs.forEach(prop => this.updateRelativeLinkInNode(node, prop, filePath, targetFolder));
      }
    });
  }

  private updateTocFileLinks(doc: Document, filePath: string, targetFolder: string) {
    const attrs = ['Link', 'Skin', 'MasterPage', 'PageLayout', 'TestFailPage', 'TestPassPage'];

    this.iterateNodes(doc.childNodes, (node: Element) => {
      if (node.nodeName === 'TocEntry' && node.hasAttributes) {
        attrs.forEach(prop => this.updateRelativeLinkInNode(node, prop, filePath, targetFolder));
      }
    });
  }

  private iterateNodes(nodes: NodeListOf<ChildNode>, callback: (child: Element) => void): void {
    nodes.forEach((child) => {
      if (child.nodeType == Node.ELEMENT_NODE) {
        callback(child as Element);
        if (child.hasChildNodes)
          this.iterateNodes(child.childNodes, callback);
      }
    });
  }

  private updateRelativeLinksInLocalStyle(node: HTMLStyleElement, fullFilePath: string, otherFolder: string): void {
    let css = node.innerHTML;
    css = this.updateRelativeLinksInCssStyle(css, fullFilePath, otherFolder);
    node.innerHTML = css;
  }

  private updateRelativeLinkInNode(node: Element, prop: string, fullFilePath: string, otherFolder: string): void {
    let link = node.getAttribute(prop);
    if (!link)
      return;

    if (prop === 'style' || prop === 'MadCap:mediastyle') {
      let css = link;
      css = this.updateRelativeLinksInCssStyle(css, fullFilePath, otherFolder);
      node.setAttribute(prop, css);
      return;
    }
    // skip links to bookmarks or queries in the same file
    if ((link.startsWith('#') || link.startsWith('?')) && prop === 'href')
      return;

    const newLink = this.updateRelativeLink(link, fullFilePath, otherFolder);
    if (newLink !== link)
      node.setAttribute(prop, newLink);
  }

  private updateRelativeLinksInCssStyle(css: string, fullFilePath: string, otherFolder: string): string {
    return CssUtils.updateLinksInCss(css, (link) => this.updateRelativeLink(link, fullFilePath, otherFolder));
  }

  private updateRelativeLink(link: string, fullFilePath: string, otherFolder: string): string {
    return tryUpdateRelativeLink(link, fullFilePath, otherFolder) ?? link;
  }

}

